export function wav16(chunks, sourceRate) {
  const length = chunks.reduce((n, c) => n + c.length, 0);
  const input = new Float32Array(length);
  let at = 0;
  chunks.forEach(c => {
    input.set(c, at);
    at += c.length;
  });
  const count = Math.min(960000, Math.floor((length * 16000) / sourceRate));
  const buffer = new ArrayBuffer(44 + count * 2),
    view = new DataView(buffer);
  const text = (offset, value) =>
    [...value].forEach((c, i) => view.setUint8(offset + i, c.charCodeAt(0)));
  text(0, "RIFF");
  view.setUint32(4, buffer.byteLength - 8, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, 16000, true);
  view.setUint32(28, 32000, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, count * 2, true);
  // Average each source interval rather than dropping samples when downsampling.
  for (let i = 0; i < count; i++) {
    const start = Math.floor((i * sourceRate) / 16000),
      end = Math.min(
        length,
        Math.max(start + 1, Math.floor(((i + 1) * sourceRate) / 16000))
      );
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    const v = Math.max(-1, Math.min(1, sum / (end - start)));
    view.setInt16(44 + i * 2, Math.round(v * (v < 0 ? 32768 : 32767)), true);
  }
  return buffer;
}
export class SpeechRecorder {
  constructor(onLevel) {
    this.onLevel = onLevel;
    this.chunks = [];
    this.closed = false;
    this.samples = 0;
  }
  async start() {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true
      },
      video: false
    });
    if (this.closed) {
      stream.getTracks().forEach(t => t.stop());
      throw new Error("录音已取消。");
    }
    this.stream = stream;
    try {
      const Context = window.AudioContext || window.webkitAudioContext;
      this.context = new Context();
      if (!this.context.audioWorklet)
        throw new Error("当前浏览器暂不支持录音，请更新或更换浏览器。");
      await this.context.resume();
      await this.context.audioWorklet.addModule(
        process.env.BASE_URL + "speech-pcm-worklet.js"
      );
      if (this.closed) throw new Error("录音已取消。");
      this.node = new AudioWorkletNode(this.context, "speech-pcm");
      this.node.port.onmessage = ({ data }) => {
        if (data.done) {
          if (this.flushed) this.flushed();
          return;
        }
        if (this.closed) return;
        const clip = data.data.slice(
          0,
          Math.max(0, Math.floor(this.context.sampleRate * 60) - this.samples)
        );
        this.samples += clip.length;
        this.chunks.push(clip);
        let sum = 0;
        for (const v of clip) sum += v * v;
        this.onLevel(
          Math.min(1, Math.sqrt(sum / Math.max(1, clip.length)) * 5)
        );
      };
      this.source = this.context.createMediaStreamSource(stream);
      this.source.connect(this.node);
      this.mute = this.context.createGain();
      this.mute.gain.value = 0;
      this.node.connect(this.mute);
      this.mute.connect(this.context.destination);
    } catch (e) {
      this.close();
      throw e;
    }
  }
  async stop() {
    if (!this.node || this.closed) throw new Error("录音已结束。");
    await new Promise(resolve => {
      this.flushed = resolve;
      this.node.port.postMessage("flush");
      setTimeout(resolve, 150);
    });
    const wav = wav16(this.chunks, this.context.sampleRate);
    this.close();
    return wav;
  }
  close() {
    this.closed = true;
    if (this.stream) this.stream.getTracks().forEach(t => t.stop());
    if (this.source) this.source.disconnect();
    if (this.node) this.node.disconnect();
    if (this.context && this.context.state !== "closed")
      this.context.close().catch(() => {});
    this.chunks = [];
  }
}
